// Weekly training load (#94): session effort x minutes, summed per week.
// Sessions with heart-rate zones are measured instead (#202), see below.
//
// This is Foster's session-RPE load, the simplest well-validated measure of
// how much training a week held. It works for lifting, where heart rate
// doesn't. It's shown for information only: the acute:chronic ratio built on
// it is a poor injury predictor (Impellizzeri 2020), so nothing here warns or
// changes the plan.
//
// `rating` on a session is effort (RPE 1-10), never enjoyment.

import { weekStart, formatRange, sessionKind } from "./activitySummary";

/** Effort x minutes for one session, or null when either is missing. */
export const effortLoad = (session) => {
  const effort = Number(session?.rating);
  const minutes = Number(session?.duration_minutes);
  if (!(effort >= 1 && effort <= 10) || !(minutes > 0)) return null;
  return Math.round(effort * minutes);
};

/**
 * Edwards' TRIMP (#202): minutes in Zones 1 to 5, weighted 1 to 5. Time below
 * Zone 1 counts nothing. Null without a zone summary, and for strength:
 * heart rate under-reads lifting, so effort stays the better measure there.
 */
export const zoneLoad = (session) => {
  const seconds = session?.telemetry_summary?.hr?.zones?.seconds;
  if (!Array.isArray(seconds) || sessionKind(session) === "strength") return null;
  const total = seconds.reduce((sum, s, zone) => sum + (zone > 0 ? (Number(s) || 0) * zone : 0), 0) / 60;
  return total > 0 ? total : null;
};

/**
 * Effort load per TRIMP point, from your own sessions that have both. Effort
 * (1-10) runs about twice the zone weight (1-5) for the same session, so 2 is
 * the default until there are CALIBRATION_MIN pairs. The median keeps one odd
 * rating from skewing it.
 */
export const DEFAULT_ZONE_SCALE = 2;
export const CALIBRATION_MIN = 3;
export const zoneScale = (sessions) => {
  const ratios = (Array.isArray(sessions) ? sessions : [])
    .map((s) => {
      const effort = effortLoad(s);
      const zones = zoneLoad(s);
      return effort && zones ? effort / zones : null;
    })
    .filter((r) => r != null)
    .sort((a, b) => a - b);
  if (ratios.length < CALIBRATION_MIN) return DEFAULT_ZONE_SCALE;
  const mid = Math.floor(ratios.length / 2);
  return ratios.length % 2 ? ratios[mid] : (ratios[mid - 1] + ratios[mid]) / 2;
};

/**
 * One session's load in effort-load units: measured from heart-rate zones
 * when it has them (scaled by `scale`), otherwise effort x minutes. Null when
 * neither is there.
 */
export const sessionLoad = (session, scale = DEFAULT_ZONE_SCALE) => {
  const zones = zoneLoad(session);
  if (zones != null) return Math.round(zones * scale);
  return effortLoad(session);
};

const zoneSeconds = (session) =>
  zoneLoad(session) != null ? session.telemetry_summary.hr.zones.seconds : null;

/**
 * One session's load split by intensity (#203): easy (Zones 1-2), threshold
 * (3-4) and VO2max (5), each zone's share of the TRIMP. A session measured by
 * effort alone can't be split, so all of it is `effort`. `time` is seconds in
 * each band, for the 80/20 check. Null when the session has no load.
 */
export const loadByIntensity = (session, scale = DEFAULT_ZONE_SCALE) => {
  const load = sessionLoad(session, scale);
  if (load == null) return null;
  const seconds = zoneSeconds(session);
  if (!seconds) return { easy: 0, threshold: 0, vo2max: 0, effort: load, time: [0, 0, 0] };
  const sec = (zone) => Number(seconds[zone]) || 0;
  const points = (zones) => (zones.reduce((sum, z) => sum + sec(z) * z, 0) / 60) * scale;
  return {
    easy: points([1, 2]),
    threshold: points([3, 4]),
    vo2max: points([5]),
    effort: 0,
    time: [sec(1) + sec(2), sec(3) + sec(4), sec(5)],
  };
};

/**
 * The last `weeks` calendar weeks (Monday to Sunday), oldest first, with this
 * week last. Weeks before the first loaded session are left out, so a short
 * history never reads as a quiet week. Pass `truncated` when the list was cut
 * off at a limit: the oldest week may then be only partly loaded, so it goes too.
 *
 * `minutes` is every session's length, split by what it trained (strength,
 * hard intervals, cardio, other): unlike load it needs no rating. `rated`
 * counts sessions with a load, from a rating or from heart-rate zones.
 * `intensity` splits the load by heart-rate band (see loadByIntensity), and
 * `zoneTime` is seconds easy, threshold and VO2max.
 *
 * @returns {Array<{ start: Date, range: string, load: number, sessions: number,
 *                   rated: number, minutes: number,
 *                   byKind: { strength: number, hard: number, cardio: number, other: number },
 *                   intensity: { easy: number, threshold: number, vo2max: number, effort: number },
 *                   zoneTime: number[],
 *                   current: boolean }>}
 */
export const weeklyLoads = (sessions, { now = Date.now(), weeks = 8, truncated = false } = {}) => {
  const list = (Array.isArray(sessions) ? sessions : []).filter(
    (s) => !Number.isNaN(new Date(s?.started_at).getTime()),
  );
  if (!list.length) return [];
  const scale = zoneScale(list);
  const oldest = Math.min(...list.map((s) => new Date(s.started_at).getTime()));
  const thisWeek = weekStart(now).getTime();

  const out = [];
  for (let i = weeks - 1; i >= 0; i--) {
    // Step back by calendar days, not milliseconds, so daylight saving can't
    // land a week on the wrong Monday.
    const monday = new Date(thisWeek);
    monday.setDate(monday.getDate() - 7 * i);
    const first = weekStart(oldest).getTime();
    if (monday.getTime() < first || (truncated && monday.getTime() === first)) continue;
    const next = new Date(monday);
    next.setDate(next.getDate() + 7);
    const end = next.getTime();
    const inWeek = list.filter((s) => {
      const t = new Date(s.started_at).getTime();
      return t >= monday.getTime() && t < end;
    });
    const loads = inWeek.map((session) => sessionLoad(session, scale)).filter((l) => l != null);
    const intensity = { easy: 0, threshold: 0, vo2max: 0, effort: 0 };
    const zoneTime = [0, 0, 0];
    for (const session of inWeek) {
      const split = loadByIntensity(session, scale);
      if (!split) continue;
      for (const k of Object.keys(intensity)) intensity[k] += split[k];
      split.time.forEach((t, i) => (zoneTime[i] += t));
    }
    for (const k of Object.keys(intensity)) intensity[k] = Math.round(intensity[k]);
    const byKind = { strength: 0, hard: 0, cardio: 0, other: 0 };
    for (const session of inWeek) {
      const mins = Number(session.duration_minutes);
      if (mins > 0) byKind[sessionKind(session)] += Math.round(mins);
    }
    out.push({
      start: monday,
      range: formatRange(monday),
      load: loads.reduce((sum, l) => sum + l, 0),
      sessions: inWeek.length,
      rated: loads.length,
      minutes: byKind.strength + byKind.hard + byKind.cardio + byKind.other,
      byKind,
      intensity,
      zoneTime,
      current: monday.getTime() === thisWeek,
    });
  }
  return out;
};

/**
 * One plain-English line comparing last week with the average of the weeks
 * before it (up to four). Only weeks with a rated session count: a week you
 * didn't rate isn't a light week, and averaging it in inflated the change.
 * Null until there are two such finished weeks, or when last week has none.
 */
export const describeLoadTrend = (weeks) => {
  const all = (weeks || []).filter((w) => !w.current);
  if (!all.length || !(all[all.length - 1].rated > 0)) return null;
  const finished = all.filter((w) => w.rated > 0);
  if (finished.length < 2) return null;
  const last = finished[finished.length - 1];
  const before = finished.slice(-5, -1);
  const average = before.reduce((sum, w) => sum + w.load, 0) / before.length;
  if (average <= 0) {
    return last.load > 0 ? `Last week: ${last.load.toLocaleString("en-AU")}.` : null;
  }
  const change = Math.round(((last.load - average) / average) * 100);
  const span = before.length === 1 ? "the week before" : `your ${before.length}-week average`;
  const head = `Last week: ${last.load.toLocaleString("en-AU")}`;
  if (Math.abs(change) < 10) return `${head}, about the same as ${span}.`;
  return `${head}, ${Math.abs(change)}% ${change > 0 ? "above" : "below"} ${span}.`;
};

/** Sessions with neither a rating nor heart rate don't count; say so when it matters. */
export const describeCoverage = (weeks) => {
  const total = (weeks || []).reduce((sum, w) => sum + w.sessions, 0);
  const rated = (weeks || []).reduce((sum, w) => sum + w.rated, 0);
  if (!total || rated === total) return null;
  return `Counts the ${rated} of ${total} sessions with an effort rating or heart rate.`;
};

/**
 * Share of sessions with a load (an effort rating, or heart-rate zones). Load
 * is only shown once most have one (EFFORT_COVERAGE), otherwise it charts the
 * rating habit, not training.
 */
export const EFFORT_COVERAGE = 0.7;
export const ratedShare = (weeks) => {
  const total = (weeks || []).reduce((sum, w) => sum + w.sessions, 0);
  const rated = (weeks || []).reduce((sum, w) => sum + w.rated, 0);
  return total ? rated / total : 0;
};

/** "This week so far: 174 min · last week 165 min." */
export const describeMinutes = (weeks) => {
  const list = weeks || [];
  const current = list.find((w) => w.current);
  const last = [...list].reverse().find((w) => !w.current);
  const parts = [];
  if (current) parts.push(`This week so far: ${current.minutes} min`);
  if (last) parts.push(`${current ? "last week" : "Last week:"} ${last.minutes} min`);
  return parts.length ? `${parts.join(" · ")}.` : null;
};

/**
 * "Last 7 weeks: 78% easy by heart-rate time. Polarised training aims for
 * about 80%." Finished weeks only. Null without an hour of zone time, which is
 * too little to call a split.
 */
export const describeIntensity = (weeks) => {
  const finished = (weeks || []).filter((w) => !w.current);
  const time = finished.reduce(
    (sum, w) => sum.map((t, i) => t + (w.zoneTime?.[i] || 0)),
    [0, 0, 0],
  );
  const total = time[0] + time[1] + time[2];
  if (total < 3600) return null;
  const span = finished.length === 1 ? "Last week" : `Last ${finished.length} weeks`;
  return `${span}: ${Math.round((time[0] / total) * 100)}% easy by heart-rate time. Polarised training aims for about 80%.`;
};

// Fitness, fatigue and form (#203): TrainingPeaks' Performance Management
// Chart on our load. Fitness and fatigue are exponentially weighted averages
// of daily load over 42 and 7 days; form is yesterday's fitness minus fatigue,
// as a share of fitness (intervals.icu's form %), so the bands don't depend on
// what units the load is in. For information only, like the load.
export const FITNESS_DAYS = 42;
export const FATIGUE_DAYS = 7;
export const FITNESS_HISTORY_DAYS = 28; // a 42-day average needs about this long to mean much

const DAY_MS = 24 * 60 * 60 * 1000;
const localDay = (t) => {
  const d = new Date(t);
  d.setHours(0, 0, 0, 0);
  return d;
};
const dayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

/**
 * Daily fitness and fatigue from the first session to today, in load per day.
 * `sessions` should cover about 12 weeks: 8 to chart and 4 to settle.
 * Null until the sessions span FITNESS_HISTORY_DAYS, or with no load at all.
 *
 * @returns {null | { days: Array<{ date: Date, fitness: number, fatigue: number }>,
 *                    fitness: number, fatigue: number, formPct: number,
 *                    fitnessWeekAgo: number }}
 */
export const fitnessSeries = (sessions, { now = Date.now() } = {}) => {
  const list = (Array.isArray(sessions) ? sessions : []).filter(
    (s) => !Number.isNaN(new Date(s?.started_at).getTime()),
  );
  if (!list.length) return null;
  const scale = zoneScale(list);
  const byDay = new Map();
  for (const session of list) {
    const load = sessionLoad(session, scale);
    if (load == null) continue;
    const key = dayKey(localDay(session.started_at));
    byDay.set(key, (byDay.get(key) || 0) + load);
  }
  if (!byDay.size) return null;
  const first = localDay(Math.min(...list.map((s) => new Date(s.started_at).getTime())));
  const today = localDay(now);
  if ((today - first) / DAY_MS < FITNESS_HISTORY_DAYS) return null;

  // Start both at the first four weeks' daily average rather than zero: the
  // list is a window, not the whole history, and a ramp up from zero would
  // read as fitness gained.
  let seed = 0;
  for (let i = 0, d = new Date(first); i < FITNESS_HISTORY_DAYS; i++, d.setDate(d.getDate() + 1)) {
    seed += byDay.get(dayKey(d)) || 0;
  }
  seed /= FITNESS_HISTORY_DAYS;
  const days = [];
  let fitness = seed;
  let fatigue = seed;
  // Step by calendar days, so daylight saving can't skip or repeat one.
  for (const d = new Date(first); d <= today; d.setDate(d.getDate() + 1)) {
    const load = byDay.get(dayKey(d)) || 0;
    fitness += (load - fitness) / FITNESS_DAYS;
    fatigue += (load - fatigue) / FATIGUE_DAYS;
    days.push({ date: new Date(d), fitness, fatigue });
  }
  const last = days[days.length - 1];
  const yesterday = days[days.length - 2] || last;
  const weekAgo = days[Math.max(0, days.length - 8)];
  return {
    days,
    fitness: last.fitness,
    fatigue: last.fatigue,
    formPct: yesterday.fitness > 0 ? ((yesterday.fitness - yesterday.fatigue) / yesterday.fitness) * 100 : 0,
    fitnessWeekAgo: weekAgo.fitness,
  };
};

/**
 * intervals.icu's form bands with plainer names. "Productive" is where
 * training builds fitness; "Overreaching" is normal in a hard block, not a
 * warning.
 */
export const formLabel = (formPct) => {
  if (formPct > 20) return "Detraining";
  if (formPct > 5) return "Fresh";
  if (formPct >= -10) return "Neutral";
  if (formPct >= -30) return "Productive";
  return "Overreaching";
};

/** "Fitness 1,181 a week (+34)": per week, the bars' scale. */
export const describeFitness = (fit) => {
  if (!fit) return null;
  const week = Math.round(fit.fitness * 7);
  const change = Math.round((fit.fitness - fit.fitnessWeekAgo) * 7);
  const sign = change > 0 ? "+" : change < 0 ? "−" : "±";
  return `Fitness ${week.toLocaleString("en-AU")} a week (${sign}${Math.abs(change)})`;
};
